import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  Button,
  Autocomplete,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import { format, addMonths, startOfMonth, endOfMonth, subMonths, parseISO } from 'date-fns';
import Layout from '../../components/layout/Layout';
import {
  CalendarMonth as CalendarIcon,
  DateRange as DateRangeIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { customerService } from '../../services/customerService';
import { supplierService } from '../../services/supplierService';
import { getBillingReportByDate, getBillingReportByMonth } from '../../services/reportService';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';
import { getAuthHeader } from '../../services/authService';
import * as XLSX from 'xlsx';

const BillingReports = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [filters, setFilters] = useState({
    customerName: ''
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  // Empty state for summary data
  const [summaryData, setSummaryData] = useState({
    totalSaleBills: 0,
    totalPurchaseBills: 0,
    totalSales: 0,
    totalPurchaseCost: 0,
    totalProfit: 0,
    totalLoss: 0,
    totalProductsSold: 0,
    totalProductsPurchased: 0,
    isProfitDay: true
  });

  // Empty state for invoice data
  const [invoices, setInvoices] = useState([]);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch customers when component mounts and fetch initial report data
  useEffect(() => {
    fetchCustomers();
    fetchSuppliers();
    // Start with daily report by default
    setDateRange(null);
    setSelectedMonth('');
    fetchBillingReport(selectedDate);
  }, []);

  // Function to fetch customers from database
  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await customerService.getAllCustomers();
      
      // Check if we have paginated response
      const customersList = response.content || response;
      
      if (Array.isArray(customersList)) {
        setCustomers(customersList);
      } else {
        console.error('Unexpected response format:', response);
        enqueueSnackbar('Failed to load customers', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      enqueueSnackbar('Failed to load customers: ' + (error.message || 'Unknown error'), { variant: 'error' });
    } finally {
      setCustomersLoading(false);
    }
  };

  // Function to fetch suppliers from database
  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const response = await supplierService.getAllSuppliers();
      
      // Check if we have paginated response
      const suppliersList = response.content || response;
      
      if (Array.isArray(suppliersList)) {
        setSuppliers(suppliersList);
      } else {
        console.error('Unexpected response format:', response);
        enqueueSnackbar('Failed to load suppliers', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      enqueueSnackbar('Failed to load suppliers: ' + (error.message || 'Unknown error'), { variant: 'error' });
    } finally {
      setSuppliersLoading(false);
    }
  };

  // Function to fetch billing report data
  const fetchBillingReport = async (date) => {
    try {
      setLoading(true);
      
      // Use the updated service function that calls the correct endpoints
      const { salesOrders, purchaseOrders } = await getBillingReportByDate(date);
      
      console.log('Processing sales data. Total orders:', salesOrders.length);
      
      // Filter orders based on selected customer or supplier before calculating totals
      let filteredSalesOrders = salesOrders;
      let filteredPurchaseOrders = purchaseOrders;
      
      if (selectedCustomer) {
        const customerNameLower = selectedCustomer.name ? 
          selectedCustomer.name.toLowerCase() : 
          (selectedCustomer.displayName ? selectedCustomer.displayName.toLowerCase() : '');
        
        filteredSalesOrders = salesOrders.filter(order => 
          (order.customer?.name || '').toLowerCase().includes(customerNameLower)
        );
        console.log('Filtered sales orders for customer:', customerNameLower, 'Count:', filteredSalesOrders.length);
      }
      
      if (selectedSupplier) {
        const supplierNameLower = selectedSupplier.name ? 
          selectedSupplier.name.toLowerCase() : 
          (selectedSupplier.companyName ? selectedSupplier.companyName.toLowerCase() : '');
        
        filteredPurchaseOrders = purchaseOrders.filter(order => 
          (order.supplier?.name || order.supplier?.companyName || '').toLowerCase().includes(supplierNameLower)
        );
        console.log('Filtered purchase orders for supplier:', supplierNameLower, 'Count:', filteredPurchaseOrders.length);
      }
      
      // Process sales data
      let totalSalesGross = 0;
      let totalSalesReturns = 0;
      const salesOrdersWithoutReturns = [];
      const salesOrdersReturns = [];
      
      // Separate regular sales and returns
      filteredSalesOrders.forEach(order => {
        console.log('Processing sales order:', order.orderNumber, 'Status:', order.status, 'Total:', order.total);
        if (order.status === 'RETURNED') {
          totalSalesReturns += (order.total || 0);
          salesOrdersReturns.push(order);
        } else {
          totalSalesGross += (order.total || 0);
          salesOrdersWithoutReturns.push(order);
        }
      });
      
      // Calculate net sales (gross sales minus returns)
      const totalSalesNet = totalSalesGross - totalSalesReturns;
      console.log('Sales calculations:', { 
        totalSalesGross, 
        totalSalesReturns, 
        totalSalesNet,
        normalOrders: salesOrdersWithoutReturns.length,
        returnOrders: salesOrdersReturns.length
      });
      
      // Calculate total products sold (from non-returned orders)
      const totalProducts = salesOrdersWithoutReturns.reduce((sum, order) => {
        const items = order.items || [];
        return sum + items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0);
      }, 0);
      
      console.log('Processing purchase data. Total orders:', filteredPurchaseOrders.length);
      
      // Process purchase data
      let totalPurchasesGross = 0;
      let totalPurchasesReturns = 0;
      const purchaseOrdersWithoutReturns = [];
      const purchaseOrdersReturns = [];
      
      // Separate regular purchases and returns
      filteredPurchaseOrders.forEach(order => {
        console.log('Processing purchase order:', order.orderNumber, 'Status:', order.status, 'Total:', order.total);
        if (order.status === 'RETURNED') {
          totalPurchasesReturns += (order.total || 0);
          purchaseOrdersReturns.push(order);
        } else {
          totalPurchasesGross += (order.total || 0);
          purchaseOrdersWithoutReturns.push(order);
        }
      });
      
      // Calculate net purchases (gross purchases minus returns)
      const totalPurchasesNet = totalPurchasesGross - totalPurchasesReturns;
      console.log('Purchase calculations:', { 
        totalPurchasesGross, 
        totalPurchasesReturns, 
        totalPurchasesNet,
        normalOrders: purchaseOrdersWithoutReturns.length,
        returnOrders: purchaseOrdersReturns.length
      });
      
      // Calculate total products purchased (from non-returned orders)
      const totalPurchasedProducts = purchaseOrdersWithoutReturns.reduce((sum, order) => {
        const items = order.items || [];
        return sum + items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0);
      }, 0);
      
      // Calculate cost of goods sold from actual sales items
      const totalCostOfGoodsSold = salesOrdersWithoutReturns.reduce((sum, order) => {
        return sum + calculatePurchaseCost(order.items || []);
      }, 0);
      
      console.log('Cost of Goods Sold calculation:', {
        salesOrdersWithoutReturns: salesOrdersWithoutReturns.length,
        totalCostOfGoodsSold
      });
      
      // Calculate profit/loss based on net values
      let totalProfit = 0;
      let totalLoss = 0;
      
      // If a customer is selected, only consider sales profit
      if (selectedCustomer) {
        const customerCostOfGoodsSold = salesOrdersWithoutReturns
          .filter(order => (order.customer?.name || '').toLowerCase().includes(
            (selectedCustomer.name || selectedCustomer.displayName || '').toLowerCase()
          ))
          .reduce((sum, order) => sum + calculatePurchaseCost(order.items || []), 0);
        totalProfit = Math.max(0, totalSalesNet - customerCostOfGoodsSold);
        totalLoss = Math.max(0, customerCostOfGoodsSold - totalSalesNet);
      } 
      // If a supplier is selected, only consider purchase as a cost (potential loss)
      else if (selectedSupplier) {
        totalProfit = 0;
        totalLoss = totalPurchasesNet;
      }
      // Otherwise use normal profit/loss calculation with cost of goods sold
      else {
        totalProfit = Math.max(0, totalSalesNet - totalCostOfGoodsSold);
        totalLoss = Math.max(0, totalCostOfGoodsSold - totalSalesNet);
      }
      
      console.log('Final calculations:', {
        totalSalesNet,
        totalPurchasesNet,
        totalCostOfGoodsSold,
        totalProfit,
        totalLoss
      });
      
      // Update summary data
      setSummaryData({
        totalSaleBills: salesOrdersWithoutReturns.length,
        totalPurchaseBills: purchaseOrdersWithoutReturns.length,
        totalSales: totalSalesNet,
        totalPurchaseCost: totalCostOfGoodsSold, // Use cost of goods sold instead of purchase orders total
        totalProfit: totalProfit,
        totalLoss: totalLoss,
        totalProductsSold: totalProducts,
        totalProductsPurchased: totalPurchasedProducts,
        isProfitDay: totalSalesNet >= totalCostOfGoodsSold
      });
      
      // Generate invoices list for the table
      const allInvoices = [];
      
      // Add sales invoices
      filteredSalesOrders.forEach(order => {
        allInvoices.push({
          invoiceNo: order.orderNumber || `SO-${order.id}`,
          dateTime: order.orderDate ? new Date(order.orderDate).toLocaleString() : 'N/A',
          customerName: order.customer?.name || 'Unknown Customer',
          totalAmount: order.total || 0,
          purchaseCost: calculatePurchaseCost(order.items || []),
          profit: (order.total || 0) - calculatePurchaseCost(order.items || []),
          type: order.status === 'RETURNED' ? 'return' : 'sale'
        });
      });
      
      // Add purchase invoices
      filteredPurchaseOrders.forEach(order => {
        allInvoices.push({
          invoiceNo: order.orderNumber || `PO-${order.id}`,
          dateTime: order.orderDate ? new Date(order.orderDate).toLocaleString() : 'N/A',
          customerName: order.supplier?.name || order.supplier?.companyName || 'Unknown Supplier',
          totalAmount: order.total || 0,
          purchaseCost: order.total || 0,
          profit: 0,
          type: order.status === 'RETURNED' ? 'purchase-return' : 'purchase'
        });
      });
      
      // Sort by date (newest first)
      allInvoices.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
      
      setInvoices(allInvoices);
      
    } catch (error) {
      console.error('Error fetching billing report:', error);
      enqueueSnackbar('Error loading billing report: ' + (error.message || 'Unknown error'), { variant: 'error' });
      
      // Reset data on error
      setSummaryData({
        totalSaleBills: 0,
        totalPurchaseBills: 0,
        totalSales: 0,
        totalPurchaseCost: 0,
        totalProfit: 0,
        totalLoss: 0,
        totalProductsSold: 0,
        totalProductsPurchased: 0,
        isProfitDay: true
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to calculate purchase cost from items
  const calculatePurchaseCost = (items) => {
    console.log('calculatePurchaseCost called with items:', items);
    
    let hasNullPurchasePrices = false;
    
    const totalCost = items.reduce((sum, item) => {
      console.log('Processing item:', item);
      console.log('Item product:', item.product);
      console.log('Item product purchasePrice:', item.product?.purchasePrice);
      console.log('Item quantity:', item.quantity);
      
      const costPrice = item.product?.purchasePrice || 0;
      const quantity = item.quantity || 0;
      const itemCost = costPrice * quantity;
      
      // Track if we encounter any null purchase prices
      if (item.product?.purchasePrice === null || item.product?.purchasePrice === undefined) {
        hasNullPurchasePrices = true;
        console.warn(`⚠️ Product "${item.product?.name}" (ID: ${item.product?.id}) has no purchase price set!`);
      }
      
      console.log(`Item cost calculation: ${costPrice} * ${quantity} = ${itemCost}`);
      
      return sum + itemCost;
    }, 0);
    
    console.log('Total calculated purchase cost:', totalCost);
    
    // Show a warning if some products don't have purchase prices
    if (hasNullPurchasePrices) {
      console.warn('⚠️ Some products are missing purchase prices, which will affect profit calculations!');
      console.warn('💡 Please set purchase prices for all products to get accurate profit/loss calculations.');
    }
    
    return totalCost;
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedMonth(''); // Reset month selection when date changes
    setDateRange(null); // Reset date range
  };

  const handleMonthChange = (e) => {
    const monthValue = e.target.value;
    setSelectedMonth(monthValue);
    
    if (monthValue) {
      const [year, month] = monthValue.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = endOfMonth(firstDay);
      
      const startDateStr = format(firstDay, 'yyyy-MM-dd');
      const endDateStr = format(lastDay, 'yyyy-MM-dd');
      
      // Set date range for display
      setDateRange({
        startDate: startDateStr,
        endDate: endDateStr,
        label: format(firstDay, 'MMMM yyyy')
      });
      
      // Fetch monthly report
      fetchMonthlyBillingReport(startDateStr, endDateStr);
    } else {
      setDateRange(null);
    }
  };

  const handleCustomerChange = (event, newValue) => {
    console.log('Customer selected:', newValue);
    setSelectedCustomer(newValue);
    // If we need to update any filter values based on customer selection
    if (newValue) {
      setFilters(prev => ({
        ...prev,
        customerName: newValue.name || newValue.displayName || ''
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        customerName: ''
      }));
    }
    
    // Apply customer filter to current data
    setPage(0); // Reset to first page when changing filters
    applyFilters();
  };
  
  const handleSupplierChange = (event, newValue) => {
    console.log('Supplier selected:', newValue);
    setSelectedSupplier(newValue);
    // Apply supplier filter to current data
    setPage(0); // Reset to first page when changing filters
    applyFilters();
  };
  
  // Apply all current filters to the data
  const applyFilters = () => {
    console.log('Applying filters - Customer:', selectedCustomer ? selectedCustomer.name : 'none', 
                'Supplier:', selectedSupplier ? selectedSupplier.name : 'none');
    // If we're viewing daily report
    if (!dateRange) {
      fetchBillingReport(selectedDate);
    } 
    // If we're viewing monthly report
    else {
      fetchMonthlyBillingReport(dateRange.startDate, dateRange.endDate);
    }
  };

  // Generate month options for the select dropdown
  const getMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Generate options for current month and 11 previous months
    for (let i = 0; i < 12; i++) {
      const date = subMonths(currentDate, i);
      const value = format(date, 'yyyy-MM');
      const label = format(date, 'MMMM yyyy');
      options.push({ value, label });
    }
    
    return options;
  };

  // Function to fetch billing report data for an entire month
  const fetchMonthlyBillingReport = async (startDate, endDate) => {
    try {
      setLoading(true);
      
      // Use the function for fetching monthly data
      const { salesOrders, purchaseOrders } = await getBillingReportByMonth(startDate, endDate);
      
      console.log(`Processing monthly data from ${startDate} to ${endDate}`);
      console.log('Total sales orders:', salesOrders.length, 'Total purchase orders:', purchaseOrders.length);
      
      // Filter orders based on selected customer or supplier before calculating totals
      let filteredSalesOrders = salesOrders;
      let filteredPurchaseOrders = purchaseOrders;
      
      if (selectedCustomer) {
        const customerNameLower = selectedCustomer.name ? 
          selectedCustomer.name.toLowerCase() : 
          (selectedCustomer.displayName ? selectedCustomer.displayName.toLowerCase() : '');
        
        filteredSalesOrders = salesOrders.filter(order => 
          (order.customer?.name || '').toLowerCase().includes(customerNameLower)
        );
        console.log('Filtered monthly sales orders for customer:', customerNameLower, 'Count:', filteredSalesOrders.length);
      }
      
      if (selectedSupplier) {
        const supplierNameLower = selectedSupplier.name ? 
          selectedSupplier.name.toLowerCase() : 
          (selectedSupplier.companyName ? selectedSupplier.companyName.toLowerCase() : '');
        
        filteredPurchaseOrders = purchaseOrders.filter(order => 
          (order.supplier?.name || order.supplier?.companyName || '').toLowerCase().includes(supplierNameLower)
        );
        console.log('Filtered monthly purchase orders for supplier:', supplierNameLower, 'Count:', filteredPurchaseOrders.length);
      }
      
      // Process sales data
      let totalSalesGross = 0;
      let totalSalesReturns = 0;
      const salesOrdersWithoutReturns = [];
      const salesOrdersReturns = [];
      
      // Separate regular sales and returns
      filteredSalesOrders.forEach(order => {
        console.log('Processing sales order:', order.orderNumber, 'Status:', order.status, 'Total:', order.total);
        if (order.status === 'RETURNED') {
          totalSalesReturns += (order.total || 0);
          salesOrdersReturns.push(order);
        } else {
          totalSalesGross += (order.total || 0);
          salesOrdersWithoutReturns.push(order);
        }
      });
      
      // Calculate net sales (gross sales minus returns)
      const totalSalesNet = totalSalesGross - totalSalesReturns;
      console.log('Monthly Sales calculations:', { 
        totalSalesGross, 
        totalSalesReturns, 
        totalSalesNet,
        normalOrders: salesOrdersWithoutReturns.length,
        returnOrders: salesOrdersReturns.length
      });
      
      // Calculate total products sold (from non-returned orders)
      const totalProducts = salesOrdersWithoutReturns.reduce((sum, order) => {
        const items = order.items || [];
        return sum + items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0);
      }, 0);
      
      // Process purchase data
      let totalPurchasesGross = 0;
      let totalPurchasesReturns = 0;
      const purchaseOrdersWithoutReturns = [];
      const purchaseOrdersReturns = [];
      
      // Separate regular purchases and returns
      filteredPurchaseOrders.forEach(order => {
        console.log('Processing purchase order:', order.orderNumber, 'Status:', order.status, 'Total:', order.total);
        if (order.status === 'RETURNED') {
          totalPurchasesReturns += (order.total || 0);
          purchaseOrdersReturns.push(order);
        } else {
          totalPurchasesGross += (order.total || 0);
          purchaseOrdersWithoutReturns.push(order);
        }
      });
      
      // Calculate net purchases (gross purchases minus returns)
      const totalPurchasesNet = totalPurchasesGross - totalPurchasesReturns;
      console.log('Monthly Purchase calculations:', { 
        totalPurchasesGross, 
        totalPurchasesReturns, 
        totalPurchasesNet,
        normalOrders: purchaseOrdersWithoutReturns.length,
        returnOrders: purchaseOrdersReturns.length
      });
      
      // Calculate total products purchased (from non-returned orders)
      const totalPurchasedProducts = purchaseOrdersWithoutReturns.reduce((sum, order) => {
        const items = order.items || [];
        return sum + items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0);
      }, 0);
      
      // Calculate cost of goods sold from actual sales items
      const totalCostOfGoodsSold = salesOrdersWithoutReturns.reduce((sum, order) => {
        return sum + calculatePurchaseCost(order.items || []);
      }, 0);
      
      console.log('Cost of Goods Sold calculation:', {
        salesOrdersWithoutReturns: salesOrdersWithoutReturns.length,
        totalCostOfGoodsSold
      });
      
      // Calculate profit/loss based on net values
      let totalProfit = 0;
      let totalLoss = 0;
      
      // If a customer is selected, only consider sales profit
      if (selectedCustomer) {
        const customerCostOfGoodsSold = salesOrdersWithoutReturns
          .filter(order => (order.customer?.name || '').toLowerCase().includes(
            (selectedCustomer.name || selectedCustomer.displayName || '').toLowerCase()
          ))
          .reduce((sum, order) => sum + calculatePurchaseCost(order.items || []), 0);
        totalProfit = Math.max(0, totalSalesNet - customerCostOfGoodsSold);
        totalLoss = Math.max(0, customerCostOfGoodsSold - totalSalesNet);
      } 
      // If a supplier is selected, only consider purchase as a cost (potential loss)
      else if (selectedSupplier) {
        totalProfit = 0;
        totalLoss = totalPurchasesNet;
      }
      // Otherwise use normal profit/loss calculation with cost of goods sold
      else {
        totalProfit = Math.max(0, totalSalesNet - totalCostOfGoodsSold);
        totalLoss = Math.max(0, totalCostOfGoodsSold - totalSalesNet);
      }
      
      console.log('Final calculations:', {
        totalSalesNet,
        totalPurchasesNet,
        totalCostOfGoodsSold,
        totalProfit,
        totalLoss
      });
      
      // Update summary data
      setSummaryData({
        totalSaleBills: salesOrdersWithoutReturns.length,
        totalPurchaseBills: purchaseOrdersWithoutReturns.length,
        totalSales: totalSalesNet,
        totalPurchaseCost: totalCostOfGoodsSold, // Use cost of goods sold instead of purchase orders total
        totalProfit: totalProfit,
        totalLoss: totalLoss,
        totalProductsSold: totalProducts,
        totalProductsPurchased: totalPurchasedProducts,
        isProfitDay: totalSalesNet >= totalCostOfGoodsSold
      });
      
      // Generate invoices list for the table
      const allInvoices = [];
      
      // Add sales invoices
      filteredSalesOrders.forEach(order => {
        allInvoices.push({
          invoiceNo: order.orderNumber || `SO-${order.id}`,
          dateTime: order.orderDate ? new Date(order.orderDate).toLocaleString() : 'N/A',
          customerName: order.customer?.name || 'Unknown Customer',
          totalAmount: order.total || 0,
          purchaseCost: calculatePurchaseCost(order.items || []),
          profit: (order.total || 0) - calculatePurchaseCost(order.items || []),
          type: order.status === 'RETURNED' ? 'return' : 'sale'
        });
      });
      
      // Add purchase invoices
      filteredPurchaseOrders.forEach(order => {
        allInvoices.push({
          invoiceNo: order.orderNumber || `PO-${order.id}`,
          dateTime: order.orderDate ? new Date(order.orderDate).toLocaleString() : 'N/A',
          customerName: order.supplier?.name || order.supplier?.companyName || 'Unknown Supplier',
          totalAmount: order.total || 0,
          purchaseCost: order.total || 0,
          profit: 0,
          type: order.status === 'RETURNED' ? 'purchase-return' : 'purchase'
        });
      });
      
      // Sort by date (newest first)
      allInvoices.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
      
      setInvoices(allInvoices);
      
    } catch (error) {
      console.error('Error fetching monthly billing report:', error);
      enqueueSnackbar('Error loading monthly report: ' + (error.message || 'Unknown error'), { variant: 'error' });
      
      // Reset data on error
      setSummaryData({
        totalSaleBills: 0,
        totalPurchaseBills: 0,
        totalSales: 0,
        totalPurchaseCost: 0,
        totalProfit: 0,
        totalLoss: 0,
        totalProductsSold: 0,
        totalProductsPurchased: 0,
        isProfitDay: true
      });
      setInvoices([]);
      setDateRange(null);
    } finally {
      setLoading(false);
    }
  };

  // Reset date range and go back to daily view
  const resetToDaily = () => {
    setDateRange(null);
    setSelectedMonth('');
    fetchBillingReport(selectedDate);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Generate row style based on invoice type
  const getRowStyle = (type) => {
    if (type === 'sale') {
      return {
        backgroundColor: 'rgba(40, 167, 69, 0.1)', // Green for sales
        '&:hover': {
          backgroundColor: 'rgba(40, 167, 69, 0.2)'
        }
      };
    } else if (type === 'purchase') {
      return {
        backgroundColor: 'rgba(220, 53, 69, 0.1)', // Red for purchases
        '&:hover': {
          backgroundColor: 'rgba(220, 53, 69, 0.2)'
        }
      };
    } else if (type === 'return' || type === 'purchase-return') {
      return {
        backgroundColor: 'rgba(255, 193, 7, 0.1)', // Yellow for all returns
        '&:hover': {
          backgroundColor: 'rgba(255, 193, 7, 0.2)'
        }
      };
    }
    return {};
  };

  // Handle view invoice action
  const handleViewInvoice = async (invoice) => {
    try {
      setSelectedInvoice(invoice);
      setViewDialogOpen(true);
      setLoadingDetails(true);

      console.log('Attempting to view details for invoice:', invoice);
      
      // Extract the database ID from the invoice
      // For simplicity, first assume that the format is "XX-YYYYMMDD-NNNN" (like PO-20250411-0001)
      // and we need to get the database ID by fetching all orders and finding one with matching orderNumber
      let orderData = null;
      const orderNumber = invoice.invoiceNo;
      
      // Handle special case for returned orders which may have "RETURN-" prefix
      const isReturn = invoice.type.includes('return') || orderNumber.startsWith('RETURN-');
      const normalizedOrderNumber = orderNumber.startsWith('RETURN-') ? 
        orderNumber.substring(7) : orderNumber; // Remove "RETURN-" prefix if present
      
      try {
        if (invoice.type.includes('purchase')) {
          // First, try to fetch all purchase orders and find the one with matching orderNumber
          const allOrdersResponse = await axios.get(
            `${API_CONFIG.BASE_URL}/purchase-orders?_t=${Date.now()}`,
            { headers: getAuthHeader() }
          );
          
          if (allOrdersResponse.data && allOrdersResponse.data.content) {
            // Look for exact match first
            let matchingOrder = allOrdersResponse.data.content.find(
              order => order.orderNumber === orderNumber
            );
            
            // If we have a return order but couldn't find direct match, try with normalized orderNumber
            if (!matchingOrder && isReturn) {
              matchingOrder = allOrdersResponse.data.content.find(
                order => order.orderNumber === normalizedOrderNumber
              );
            }
            
            if (matchingOrder) {
              // If we found a matching order, we can use its database ID
              const dbId = matchingOrder.id;
              
              // Now fetch the full order details using the database ID
              const detailsResponse = await axios.get(
                `${API_CONFIG.BASE_URL}/purchase-orders/${dbId}?_t=${Date.now()}`,
                { headers: getAuthHeader() }
              );
              
              orderData = detailsResponse.data;
              console.log('Found purchase order by ID:', orderData);
            } else {
              // If no matching order is found, try the fallback approach
              const numericId = orderNumber.split('-').pop();
              
              const response = await axios.get(
                `${API_CONFIG.BASE_URL}/purchase-orders/${numericId}?_t=${Date.now()}`,
                { headers: getAuthHeader() }
              );
              
              orderData = response.data;
            }
          }
        } else {
          // For sales orders
          // First, try to fetch all sales orders and find the one with matching orderNumber
          const allOrdersResponse = await axios.get(
            `${API_CONFIG.BASE_URL}/sales-orders?_t=${Date.now()}`,
            { headers: getAuthHeader() }
          );
          
          if (allOrdersResponse.data && allOrdersResponse.data.content) {
            // Look for exact match first
            let matchingOrder = allOrdersResponse.data.content.find(
              order => order.orderNumber === orderNumber
            );
            
            // If we have a return order but couldn't find direct match, try with normalized orderNumber
            if (!matchingOrder && isReturn) {
              matchingOrder = allOrdersResponse.data.content.find(
                order => order.orderNumber === normalizedOrderNumber
              );
            }
            
            if (matchingOrder) {
              // If we found a matching order, we can use its database ID
              const dbId = matchingOrder.id;
              
              // Now fetch the full order details using the database ID
              const detailsResponse = await axios.get(
                `${API_CONFIG.BASE_URL}/sales-orders/${dbId}?_t=${Date.now()}`,
                { headers: getAuthHeader() }
              );
              
              orderData = detailsResponse.data;
              console.log('Found sales order by ID:', orderData);
            } else {
              // If no matching order is found, try the fallback approach
              const numericId = orderNumber.split('-').pop();
              
              const response = await axios.get(
                `${API_CONFIG.BASE_URL}/sales-orders/${numericId}?_t=${Date.now()}`,
                { headers: getAuthHeader() }
              );
              
              orderData = response.data;
            }
          }
        }
      } catch (error) {
        console.error('Error finding order details:', error);
        throw error;
      }

      if (!orderData) {
        throw new Error(`Could not find order details for: ${orderNumber}`);
      }

      console.log('Order details received:', orderData);
      setOrderDetails(orderData);
    } catch (error) {
      console.error('Error fetching order details:', error);
      enqueueSnackbar(`Error loading order details: ${error.message || 'Unknown error'}`, { 
        variant: 'error' 
      });
      setOrderDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      // Prepare export data from invoices
      const exportData = invoices.map((inv, idx) => ({
        'S.No': idx + 1,
        'Invoice No': inv.invoiceNo,
        'Date/Time': inv.dateTime,
        'Customer/Supplier': inv.customerName,
        'Total Amount': inv.totalAmount,
        'Purchase Cost': inv.purchaseCost,
        'Profit': inv.profit,
        'Type': inv.type
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 5 }, { wch: 18 }, { wch: 22 }, { wch: 28 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 10 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.sheet_add_aoa(ws, [
        ['Billing Report'],
        ['Generated on: ' + new Date().toLocaleString()],
        ['Period: ' + (dateRange ? `${dateRange.startDate} to ${dateRange.endDate}` : selectedDate)],
        ['']
      ], { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, ws, 'Billing Report');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `billing_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Billing report exported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to export billing report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Export Button Row */}
        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportToExcel}
              disabled={isExporting || invoices.length === 0}
              startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Grid>
        </Grid>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {dateRange ? `Monthly Billing Report: ${dateRange.label}` : 'Daily Billing Report'}
        </Typography>
        
        {(selectedCustomer || selectedSupplier) && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {selectedCustomer && (
              <Chip 
                label={`Filtered by Customer: ${selectedCustomer.name || selectedCustomer.displayName}`}
                color="primary"
                onDelete={() => handleCustomerChange(null, null)}
                sx={{ my: 0.5 }}
              />
            )}
            {selectedSupplier && (
              <Chip 
                label={`Filtered by Supplier: ${selectedSupplier.name || selectedSupplier.companyName}`}
                color="secondary"
                onDelete={() => handleSupplierChange(null, null)}
                sx={{ my: 0.5 }}
              />
            )}
          </Box>
        )}
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Select Date"
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled={loading || !!dateRange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Select Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    label="Select Month"
                    disabled={loading}
                  >
                    <MenuItem value="">None</MenuItem>
                    {getMonthOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={dateRange ? <DateRangeIcon /> : <CalendarIcon />}
                  onClick={() => applyFilters()}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : (dateRange ? 'Generate Monthly Report' : 'Generate Daily Report')}
                </Button>
              </Grid>
              {dateRange && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Chip 
                      icon={<DateRangeIcon />} 
                      label={`Viewing data for ${dateRange.label} (${dateRange.startDate} to ${dateRange.endDate})`}
                      color="primary"
                      variant="outlined"
                      onDelete={resetToDaily}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Sales Related Cards */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedSupplier ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedCustomer ? `${selectedCustomer.name}'s Sale Bills` : 'Total Sale Bills'}
                </Typography>
                <Typography variant="h5">{summaryData.totalSaleBills} Bills</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Purchase Related Cards */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedCustomer ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedSupplier ? `${selectedSupplier.name || selectedSupplier.companyName}'s Purchase Bills` : 'Total Purchase Bills'}
                </Typography>
                <Typography variant="h5">{summaryData.totalPurchaseBills} Bills</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Sales Amount Card */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedSupplier ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedCustomer ? `${selectedCustomer.name}'s Total Sales` : 'Total Sales Amount'}
                </Typography>
                <Typography variant="h5">${summaryData.totalSales.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Purchase Amount Card */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedCustomer ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedSupplier ? `${selectedSupplier.name || selectedSupplier.companyName}'s Purchase Cost` : 'Total Purchase Cost'}
                </Typography>
                <Typography variant="h5">${summaryData.totalPurchaseCost.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Profit Card */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedSupplier ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedCustomer ? `${selectedCustomer.name}'s Profit` : 'Total Profit'}
                </Typography>
                <Typography variant="h5" color="success.main">${summaryData.totalProfit.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Loss Card */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedCustomer ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedCustomer ? `${selectedCustomer.name}'s Loss` : 'Total Loss'}
                </Typography>
                <Typography variant="h5" color="error.main">${summaryData.totalLoss.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Products Sold Card */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedSupplier ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedCustomer ? `Products Sold to ${selectedCustomer.name}` : 'Total Products Sold'}
                </Typography>
                <Typography variant="h5">{summaryData.totalProductsSold.toLocaleString()} Items</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Products Purchased Card */}
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: selectedCustomer ? 'none' : 'block' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  {selectedSupplier ? `Products Purchased from ${selectedSupplier.name || selectedSupplier.companyName}` : 'Total Products Purchased'}
                </Typography>
                <Typography variant="h5">{summaryData.totalProductsPurchased.toLocaleString()} Items</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Filters</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    id="customer-filter"
                    options={customers}
                    getOptionLabel={(option) => option.name || option.displayName || ''}
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                    loading={customersLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {customersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name || option.displayName || option.id}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    id="supplier-filter"
                    options={suppliers}
                    getOptionLabel={(option) => option.name || option.companyName || ''}
                    value={selectedSupplier}
                    onChange={handleSupplierChange}
                    loading={suppliersLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {suppliersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name || option.companyName || option.id}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Invoice No</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Customer/Supplier</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
              {dateRange && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ backgroundColor: 'primary.light', color: 'white' }}>
                    Showing transactions from {dateRange.startDate} to {dateRange.endDate}
                  </TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No data available for the selected date.
                  </TableCell>
                </TableRow>
              ) : (
                (rowsPerPage > 0
                  ? invoices
                      .filter(invoice => {
                        if (selectedCustomer) {
                          return !invoice.type.includes('purchase') && 
                                 invoice.customerName === (selectedCustomer.name || selectedCustomer.displayName);
                        }
                        if (selectedSupplier) {
                          return invoice.type.includes('purchase') && 
                                 invoice.customerName === (selectedSupplier.name || selectedSupplier.companyName);
                        }
                        return true;
                      })
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : invoices
                ).map((invoice, index) => (
                  <TableRow 
                    key={index}
                    sx={getRowStyle(invoice.type)}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{invoice.invoiceNo}</TableCell>
                    <TableCell>{invoice.dateTime}</TableCell>
                    <TableCell>
                      {invoice.type.includes('purchase') ? 
                        <span><strong>Supplier:</strong> {invoice.customerName}</span> : 
                        <span><strong>Customer:</strong> {invoice.customerName}</span>
                      }
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={invoices.filter(invoice => {
              if (selectedCustomer) {
                return !invoice.type.includes('purchase') && 
                       invoice.customerName === (selectedCustomer.name || selectedCustomer.displayName);
              }
              if (selectedSupplier) {
                return invoice.type.includes('purchase') && 
                       invoice.customerName === (selectedSupplier.name || selectedSupplier.companyName);
              }
              return true;
            }).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      {/* Order Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant="h6" component="span">
            {selectedInvoice?.type.includes('purchase') ? 'Purchase' : 'Sales'} Order Details
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Invoice No: {selectedInvoice?.invoiceNo}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : orderDetails ? (
            <Box>
              {/* Order Information */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Order Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(orderDetails.orderDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip 
                        label={orderDetails.status} 
                        color={
                          orderDetails.status === 'RETURNED' ? 'error' :
                          orderDetails.status === 'DELIVERED' || orderDetails.status === 'RECEIVED_COMPLETE' ? 'success' :
                          'warning'
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {selectedInvoice?.type.includes('purchase') ? 'Supplier' : 'Customer'}
                      </Typography>
                      <Typography variant="body1">
                        {selectedInvoice?.type.includes('purchase') ? 
                          orderDetails.supplier?.companyName || orderDetails.supplier?.name :
                          orderDetails.customer?.name}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Items Table */}
              <Typography variant="subtitle1" gutterBottom>
                Order Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Tax</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDetails.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.product?.name}
                          <Typography variant="caption" display="block" color="text.secondary">
                            SKU: {item.product?.sku || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{item.tax ? `${item.tax.rate}%` : '0%'}</TableCell>
                        <TableCell align="right">{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Order Summary */}
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={1} justifyContent="flex-end">
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1" align="right">
                      {formatCurrency(orderDetails.subtotal)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Tax Amount:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1" align="right">
                      {formatCurrency(orderDetails.taxAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle1" fontWeight="bold" align="right">
                      {formatCurrency(orderDetails.total)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Information */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Payment Information
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Method
                        </Typography>
                        <Typography variant="body1">
                          {orderDetails.paymentMethod || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Status
                        </Typography>
                        <Chip 
                          label={orderDetails.paymentStatus || 'N/A'} 
                          color={orderDetails.paymentStatus === 'PAID' ? 'success' : 'warning'}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          ) : (
            <Typography color="error" align="center">
              Failed to load order details
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default BillingReports; 